import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Button, Card, CardContent,
  List, ListItem, ListItemIcon, ListItemText, Chip, Paper,
  Switch, FormControlLabel, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import { motion } from 'framer-motion';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StarIcon from '@mui/icons-material/Star';
import LockIcon from '@mui/icons-material/Lock';

const signData = {
  aries: {
    symbol: '♈',
    color: '#FF6B6B',
    element: 'Fire',
    dates: 'Mar 21 - Apr 19',
    ruler: 'Mars',
    youtubeId: 'qGmfI0uqzx0',
    customMessage: 'Neptune + Saturn in YOUR sign = biggest transformation in 165 years!',
    keyThemes: [],
    majorTransits: [
      { title: '2026 Cosmic Overview', subtitle: 'Watch free overview & get extended deep dive', type: 'overview', slug: 'overview', enabled: true },
      { title: '2026 Monthly Astrological Forecast', subtitle: 'Monthly planetary movements and their impact on your life', type: 'monthly', slug: 'monthly', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Annular Solar Eclipse in Aquarius - Feb 17th', type: 'event', slug: 'solar-eclipse-aquarius', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Lunar Eclipse in Virgo - Mar 3rd', type: 'event', slug: 'lunar-eclipse-virgo', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Solar Eclipse in Leo - Aug 12th', type: 'event', slug: 'solar-eclipse-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Neptune-Saturn Conjunction', type: 'event', slug: 'neptune-saturn-conjunction', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Uranus in Gemini', type: 'event', slug: 'uranus-gemini', enabled: true },
      { title: 'Major Astrological Event', subtitle: 'Jupiter in Leo', type: 'event', slug: 'jupiter-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Nodes changing signs', type: 'event', slug: 'nodes-changing', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Venus Retrograde', type: 'event', slug: 'venus-retrograde', enabled: false }
    ]
  },
  taurus: {
    symbol: '♉',
    color: '#4ECDC4',
    element: 'Earth',
    dates: 'Apr 20 - May 20',
    ruler: 'Venus',
    youtubeId: 'JPwtuNcgW3U',
    customMessage: '7-year identity revolution completes, 7-year wealth revolution begins!',
    keyThemes: [],
    majorTransits: [
      { title: '2026 Cosmic Overview', subtitle: 'Watch free overview & get extended deep dive', type: 'overview', slug: 'overview', enabled: true },
      { title: '2026 Monthly Astrological Forecast', subtitle: 'Monthly planetary movements and their impact on your life', type: 'monthly', slug: 'monthly', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Annular Solar Eclipse in Aquarius - Feb 17th', type: 'event', slug: 'solar-eclipse-aquarius', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Lunar Eclipse in Virgo - Mar 3rd', type: 'event', slug: 'lunar-eclipse-virgo', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Solar Eclipse in Leo - Aug 12th', type: 'event', slug: 'solar-eclipse-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Neptune-Saturn Conjunction', type: 'event', slug: 'neptune-saturn-conjunction', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Uranus in Gemini', type: 'event', slug: 'uranus-gemini', enabled: true },
      { title: 'Major Astrological Event', subtitle: 'Jupiter in Leo', type: 'event', slug: 'jupiter-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Nodes changing signs', type: 'event', slug: 'nodes-changing', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Venus Retrograde', type: 'event', slug: 'venus-retrograde', enabled: false }
    ]
  },
  gemini: {
    symbol: '♊',
    color: '#FFE66D',
    element: 'Air',
    dates: 'May 21 - Jun 20',
    ruler: 'Mercury',
    youtubeId: '6wLIOkEIZZY',
    customMessage: 'Uranus in YOUR sign = 7-year identity revolution begins!',
    keyThemes: [],
    majorTransits: [
      { title: '2026 Cosmic Overview', subtitle: 'Watch free overview & get extended deep dive', type: 'overview', slug: 'overview', enabled: true },
      { title: '2026 Monthly Astrological Forecast', subtitle: 'Monthly planetary movements and their impact on your life', type: 'monthly', slug: 'monthly', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Annular Solar Eclipse in Aquarius - Feb 17th', type: 'event', slug: 'solar-eclipse-aquarius', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Lunar Eclipse in Virgo - Mar 3rd', type: 'event', slug: 'lunar-eclipse-virgo', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Solar Eclipse in Leo - Aug 12th', type: 'event', slug: 'solar-eclipse-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Neptune-Saturn Conjunction', type: 'event', slug: 'neptune-saturn-conjunction', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Uranus in Gemini', type: 'event', slug: 'uranus-gemini', enabled: true },
      { title: 'Major Astrological Event', subtitle: 'Jupiter in Leo', type: 'event', slug: 'jupiter-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Nodes changing signs', type: 'event', slug: 'nodes-changing', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Venus Retrograde', type: 'event', slug: 'venus-retrograde', enabled: false }
    ]
  },
  cancer: {
    symbol: '♋',
    color: '#A8E6CF',
    element: 'Water',
    dates: 'Jun 21 - Jul 22',
    ruler: 'Moon',
    youtubeId: 'mTv0GJKa_ac',
    customMessage: 'Neptune + Saturn conjunct in 10th house = career & spiritual awakening!',
    keyThemes: [],
    majorTransits: [
      { title: '2026 Cosmic Overview', subtitle: 'Watch free overview & get extended deep dive', type: 'overview', slug: 'overview', enabled: true },
      { title: '2026 Monthly Astrological Forecast', subtitle: 'Monthly planetary movements and their impact on your life', type: 'monthly', slug: 'monthly', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Annular Solar Eclipse in Aquarius - Feb 17th', type: 'event', slug: 'solar-eclipse-aquarius', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Lunar Eclipse in Virgo - Mar 3rd', type: 'event', slug: 'lunar-eclipse-virgo', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Solar Eclipse in Leo - Aug 12th', type: 'event', slug: 'solar-eclipse-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Neptune-Saturn Conjunction', type: 'event', slug: 'neptune-saturn-conjunction', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Uranus in Gemini', type: 'event', slug: 'uranus-gemini', enabled: true },
      { title: 'Major Astrological Event', subtitle: 'Jupiter in Leo', type: 'event', slug: 'jupiter-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Nodes changing signs', type: 'event', slug: 'nodes-changing', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Venus Retrograde', type: 'event', slug: 'venus-retrograde', enabled: false }
    ]
  },
  leo: {
    symbol: '♌',
    color: '#FFD93D',
    element: 'Fire',
    dates: 'Jul 23 - Aug 22',
    ruler: 'Sun',
    youtubeId: '6lVBoEhMQX4',
    customMessage: 'Jupiter in YOUR sign + Total Solar Eclipse in 7th house = expansion & partnership transformation!',
    keyThemes: [],
    majorTransits: [
      { title: '2026 Cosmic Overview', subtitle: 'Watch free overview & get extended deep dive', type: 'overview', slug: 'overview', enabled: true },
      { title: '2026 Monthly Astrological Forecast', subtitle: 'Monthly planetary movements and their impact on your life', type: 'monthly', slug: 'monthly', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Annular Solar Eclipse in Aquarius - Feb 17th', type: 'event', slug: 'solar-eclipse-aquarius', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Lunar Eclipse in Virgo - Mar 3rd', type: 'event', slug: 'lunar-eclipse-virgo', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Solar Eclipse in Leo - Aug 12th', type: 'event', slug: 'solar-eclipse-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Neptune-Saturn Conjunction', type: 'event', slug: 'neptune-saturn-conjunction', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Uranus in Gemini', type: 'event', slug: 'uranus-gemini', enabled: true },
      { title: 'Major Astrological Event', subtitle: 'Jupiter in Leo', type: 'event', slug: 'jupiter-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Nodes changing signs', type: 'event', slug: 'nodes-changing', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Venus Retrograde', type: 'event', slug: 'venus-retrograde', enabled: false }
    ]
  },
  virgo: {
    symbol: '♍',
    color: '#95E1D3',
    element: 'Earth',
    dates: 'Aug 23 - Sep 22',
    ruler: 'Mercury',
    youtubeId: '5lPWnFnj52s',
    customMessage: 'Total Lunar Eclipse in YOUR sign = major release & transformation!',
    keyThemes: [],
    majorTransits: [
      { title: '2026 Cosmic Overview', subtitle: 'Watch free overview & get extended deep dive', type: 'overview', slug: 'overview', enabled: true },
      { title: '2026 Monthly Astrological Forecast', subtitle: 'Monthly planetary movements and their impact on your life', type: 'monthly', slug: 'monthly', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Annular Solar Eclipse in Aquarius - Feb 17th', type: 'event', slug: 'solar-eclipse-aquarius', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Lunar Eclipse in Virgo - Mar 3rd', type: 'event', slug: 'lunar-eclipse-virgo', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Solar Eclipse in Leo - Aug 12th', type: 'event', slug: 'solar-eclipse-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Neptune-Saturn Conjunction', type: 'event', slug: 'neptune-saturn-conjunction', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Uranus in Gemini', type: 'event', slug: 'uranus-gemini', enabled: true },
      { title: 'Major Astrological Event', subtitle: 'Jupiter in Leo', type: 'event', slug: 'jupiter-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Nodes changing signs', type: 'event', slug: 'nodes-changing', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Venus Retrograde', type: 'event', slug: 'venus-retrograde', enabled: false }
    ]
  },
  libra: {
    symbol: '♎',
    color: '#FFA8B5',
    element: 'Air',
    dates: 'Sep 23 - Oct 22',
    ruler: 'Venus',
    youtubeId: 'IoECd8ex7GE',
    customMessage: 'Grand air trine activates: friendships expand, creativity transforms, wisdom awakens!',
    keyThemes: [],
    majorTransits: [
      { title: '2026 Cosmic Overview', subtitle: 'Watch free overview & get extended deep dive', type: 'overview', slug: 'overview', enabled: true },
      { title: '2026 Monthly Astrological Forecast', subtitle: 'Monthly planetary movements and their impact on your life', type: 'monthly', slug: 'monthly', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Annular Solar Eclipse in Aquarius - Feb 17th', type: 'event', slug: 'solar-eclipse-aquarius', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Lunar Eclipse in Virgo - Mar 3rd', type: 'event', slug: 'lunar-eclipse-virgo', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Solar Eclipse in Leo - Aug 12th', type: 'event', slug: 'solar-eclipse-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Neptune-Saturn Conjunction', type: 'event', slug: 'neptune-saturn-conjunction', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Uranus in Gemini', type: 'event', slug: 'uranus-gemini', enabled: true },
      { title: 'Major Astrological Event', subtitle: 'Jupiter in Leo', type: 'event', slug: 'jupiter-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Nodes changing signs', type: 'event', slug: 'nodes-changing', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Venus Retrograde', type: 'event', slug: 'venus-retrograde', enabled: false }
    ]
  },
  scorpio: {
    symbol: '♏',
    color: '#B983FF',
    element: 'Water',
    dates: 'Oct 23 - Nov 21',
    ruler: 'Pluto/Mars',
    youtubeId: 'CTMyonhE_sU',
    customMessage: 'Pluto in Aquarius = deep transformation of home & family foundations!',
    keyThemes: [],
    majorTransits: [
      { title: '2026 Cosmic Overview', subtitle: 'Watch free overview & get extended deep dive', type: 'overview', slug: 'overview', enabled: true },
      { title: '2026 Monthly Astrological Forecast', subtitle: 'Monthly planetary movements and their impact on your life', type: 'monthly', slug: 'monthly', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Annular Solar Eclipse in Aquarius - Feb 17th', type: 'event', slug: 'solar-eclipse-aquarius', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Lunar Eclipse in Virgo - Mar 3rd', type: 'event', slug: 'lunar-eclipse-virgo', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Solar Eclipse in Leo - Aug 12th', type: 'event', slug: 'solar-eclipse-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Neptune-Saturn Conjunction', type: 'event', slug: 'neptune-saturn-conjunction', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Uranus in Gemini', type: 'event', slug: 'uranus-gemini', enabled: true },
      { title: 'Major Astrological Event', subtitle: 'Jupiter in Leo', type: 'event', slug: 'jupiter-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Nodes changing signs', type: 'event', slug: 'nodes-changing', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Venus Retrograde', type: 'event', slug: 'venus-retrograde', enabled: false }
    ]
  },
  sagittarius: {
    symbol: '♐',
    color: '#FF8C42',
    element: 'Fire',
    dates: 'Nov 22 - Dec 21',
    ruler: 'Jupiter',
    youtubeId: 'pe515Q4bsXc',
    customMessage: 'Jupiter in 9th house + Uranus in 7th house = adventure & relationship breakthrough!',
    keyThemes: [],
    majorTransits: [
      { title: '2026 Cosmic Overview', subtitle: 'Watch free overview & get extended deep dive', type: 'overview', slug: 'overview', enabled: true },
      { title: '2026 Monthly Astrological Forecast', subtitle: 'Monthly planetary movements and their impact on your life', type: 'monthly', slug: 'monthly', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Annular Solar Eclipse in Aquarius - Feb 17th', type: 'event', slug: 'solar-eclipse-aquarius', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Lunar Eclipse in Virgo - Mar 3rd', type: 'event', slug: 'lunar-eclipse-virgo', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Solar Eclipse in Leo - Aug 12th', type: 'event', slug: 'solar-eclipse-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Neptune-Saturn Conjunction', type: 'event', slug: 'neptune-saturn-conjunction', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Uranus in Gemini', type: 'event', slug: 'uranus-gemini', enabled: true },
      { title: 'Major Astrological Event', subtitle: 'Jupiter in Leo', type: 'event', slug: 'jupiter-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Nodes changing signs', type: 'event', slug: 'nodes-changing', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Venus Retrograde', type: 'event', slug: 'venus-retrograde', enabled: false }
    ]
  },
  capricorn: {
    symbol: '♑',
    color: '#6C5B7B',
    element: 'Earth',
    dates: 'Dec 22 - Jan 19',
    ruler: 'Saturn',
    youtubeId: 'tr9_8jVxHfI',
    customMessage: 'Neptune + Saturn conjunct in 4th house = home & family transformation!',
    keyThemes: [],
    majorTransits: [
      { title: '2026 Cosmic Overview', subtitle: 'Watch free overview & get extended deep dive', type: 'overview', slug: 'overview', enabled: true },
      { title: '2026 Monthly Astrological Forecast', subtitle: 'Monthly planetary movements and their impact on your life', type: 'monthly', slug: 'monthly', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Annular Solar Eclipse in Aquarius - Feb 17th', type: 'event', slug: 'solar-eclipse-aquarius', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Lunar Eclipse in Virgo - Mar 3rd', type: 'event', slug: 'lunar-eclipse-virgo', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Solar Eclipse in Leo - Aug 12th', type: 'event', slug: 'solar-eclipse-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Neptune-Saturn Conjunction', type: 'event', slug: 'neptune-saturn-conjunction', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Uranus in Gemini', type: 'event', slug: 'uranus-gemini', enabled: true },
      { title: 'Major Astrological Event', subtitle: 'Jupiter in Leo', type: 'event', slug: 'jupiter-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Nodes changing signs', type: 'event', slug: 'nodes-changing', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Venus Retrograde', type: 'event', slug: 'venus-retrograde', enabled: false }
    ]
  },
  aquarius: {
    symbol: '♒',
    color: '#4ECDC4',
    element: 'Air',
    dates: 'Jan 20 - Feb 18',
    ruler: 'Uranus/Saturn',
    youtubeId: 'mKbLth2xSwk',
    customMessage: 'Pluto + Annual Solar Eclipse in YOUR sign = total life revolution!',
    keyThemes: [],
    majorTransits: [
      { title: '2026 Cosmic Overview', subtitle: 'Watch free overview & get extended deep dive', type: 'overview', slug: 'overview', enabled: true },
      { title: '2026 Monthly Astrological Forecast', subtitle: 'Monthly planetary movements and their impact on your life', type: 'monthly', slug: 'monthly', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Annular Solar Eclipse in Aquarius - Feb 17th', type: 'event', slug: 'solar-eclipse-aquarius', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Lunar Eclipse in Virgo - Mar 3rd', type: 'event', slug: 'lunar-eclipse-virgo', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Solar Eclipse in Leo - Aug 12th', type: 'event', slug: 'solar-eclipse-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Neptune-Saturn Conjunction', type: 'event', slug: 'neptune-saturn-conjunction', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Uranus in Gemini', type: 'event', slug: 'uranus-gemini', enabled: true },
      { title: 'Major Astrological Event', subtitle: 'Jupiter in Leo', type: 'event', slug: 'jupiter-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Nodes changing signs', type: 'event', slug: 'nodes-changing', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Venus Retrograde', type: 'event', slug: 'venus-retrograde', enabled: false }
    ]
  },
  pisces: {
    symbol: '♓',
    color: '#C8A2C8',
    element: 'Water',
    dates: 'Feb 19 - Mar 20',
    ruler: 'Neptune/Jupiter',
    youtubeId: 'ciPMXa06axI',
    customMessage: 'Neptune + Saturn move into 2nd house + Jupiter in 5th house = wealth & creativity revolution!',
    keyThemes: [],
    majorTransits: [
      { title: '2026 Cosmic Overview', subtitle: 'Watch free overview & get extended deep dive', type: 'overview', slug: 'overview', enabled: true },
      { title: '2026 Monthly Astrological Forecast', subtitle: 'Monthly planetary movements and their impact on your life', type: 'monthly', slug: 'monthly', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Annular Solar Eclipse in Aquarius - Feb 17th', type: 'event', slug: 'solar-eclipse-aquarius', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Lunar Eclipse in Virgo - Mar 3rd', type: 'event', slug: 'lunar-eclipse-virgo', enabled: true },
      { title: '2026 Major Astrological Event', subtitle: 'Total Solar Eclipse in Leo - Aug 12th', type: 'event', slug: 'solar-eclipse-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Neptune-Saturn Conjunction', type: 'event', slug: 'neptune-saturn-conjunction', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Uranus in Gemini', type: 'event', slug: 'uranus-gemini', enabled: true },
      { title: 'Major Astrological Event', subtitle: 'Jupiter in Leo', type: 'event', slug: 'jupiter-leo', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Nodes changing signs', type: 'event', slug: 'nodes-changing', enabled: false },
      { title: 'Major Astrological Event', subtitle: 'Venus Retrograde', type: 'event', slug: 'venus-retrograde', enabled: false }
    ]
  }
};

const months = [
  { name: 'January', number: 1 },
  { name: 'February', number: 2 },
  { name: 'March', number: 3 },
  { name: 'April', number: 4 },
  { name: 'May', number: 5 },
  { name: 'June', number: 6 },
  { name: 'July', number: 7 },
  { name: 'August', number: 8 },
  { name: 'September', number: 9 },
  { name: 'October', number: 10 },
  { name: 'November', number: 11 },
  { name: 'December', number: 12 }
];

const allSigns = ['aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo', 'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces'];

const RisingSignLanding = () => {
  const { sign } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdminMode = searchParams.get('admin') === 'true';
  const data = signData[sign.toLowerCase()] || signData.aries;

  // Load enabled states: code defaults take precedence when enabled=true
  const getInitialEnabledStates = () => {
    const saved = localStorage.getItem(`transit-enabled-${sign}`);
    const savedStates = saved ? JSON.parse(saved) : {};
    return data.majorTransits.reduce((acc, transit, idx) => {
      acc[idx] = transit.enabled || savedStates[idx] || false;
      return acc;
    }, {});
  };

  // Load monthly enabled states: code defaults take precedence when enabled=true
  const getInitialMonthlyEnabledStates = () => {
    const saved = localStorage.getItem(`monthly-enabled-${sign}`);
    const savedStates = saved ? JSON.parse(saved) : {};
    return months.reduce((acc, month) => {
      acc[month.number] = true || savedStates[month.number] || false;
      return acc;
    }, {});
  };

  // Load dashboard enabled state from localStorage
  const getInitialDashboardEnabled = () => {
    const saved = localStorage.getItem('dashboard-enabled');
    if (saved) {
      return JSON.parse(saved);
    }
    // Default: disabled for public users
    return false;
  };

  const [enabledStates, setEnabledStates] = useState(getInitialEnabledStates());
  const [monthlyEnabledStates, setMonthlyEnabledStates] = useState(getInitialMonthlyEnabledStates());
  const [dashboardEnabled, setDashboardEnabled] = useState(getInitialDashboardEnabled());
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  // Save to localStorage when states change
  const handleToggle = (index) => {
    const newStates = { ...enabledStates, [index]: !enabledStates[index] };
    setEnabledStates(newStates);
    localStorage.setItem(`transit-enabled-${sign}`, JSON.stringify(newStates));
  };

  // Save to localStorage when monthly states change
  const handleMonthlyToggle = (monthNumber) => {
    const newStates = { ...monthlyEnabledStates, [monthNumber]: !monthlyEnabledStates[monthNumber] };
    setMonthlyEnabledStates(newStates);
    localStorage.setItem(`monthly-enabled-${sign}`, JSON.stringify(newStates));
  };

  // Save to localStorage when dashboard state changes
  const handleDashboardToggle = () => {
    const newState = !dashboardEnabled;
    setDashboardEnabled(newState);
    localStorage.setItem('dashboard-enabled', JSON.stringify(newState));
  };

  return (
    <Box sx={{ bgcolor: '#0a0a0f', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${data.color}22 0%, ${data.color}44 100%)`,
          py: { xs: 3, md: 6 },
          borderBottom: `4px solid ${data.color}`
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
            <Grid item xs={12}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h1" sx={{
                    color: data.color,
                    fontSize: { xs: '2.5rem', sm: '3rem', md: '4rem' },
                    mr: { xs: 1, md: 2 }
                  }}>
                    {data.symbol}
                  </Typography>
                  <Box>
                    <Typography variant="h3" sx={{
                      fontWeight: 700,
                      textTransform: 'capitalize',
                      fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' }
                    }}>
                      {sign} Rising
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" sx={{
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}>
                      Your Complete 2026 Forecast
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, md: 2 }, mb: 3 }}>
                  <Chip label={data.element} sx={{ bgcolor: data.color, color: 'white', fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
                  <Chip label={`Ruler: ${data.ruler}`} variant="outlined" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
                  <Chip label={data.dates} variant="outlined" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }} />
                </Box>

                {data.customMessage ? (
                  <Typography variant="h5" paragraph sx={{
                    mb: 3,
                    fontWeight: 600,
                    color: data.color,
                    fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
                  }}>
                    {data.customMessage}
                  </Typography>
                ) : data.keyThemes && data.keyThemes.length > 0 ? (
                  <>
                    <Typography variant="h5" paragraph sx={{
                      mb: 3,
                      fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
                    }}>
                      2026 Key Themes for You:
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {data.keyThemes.map((theme, idx) => (
                        <Paper key={idx} sx={{ px: 3, py: 1.5, bgcolor: '#1a1a2e', border: '1px solid rgba(201,168,124,0.12)' }}>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <StarIcon sx={{ color: data.color, mr: 1 }} />
                            {theme}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  </>
                ) : null}
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Admin Control Panel */}
      {isAdminMode && (
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <Accordion
            expanded={showAdminPanel}
            onChange={() => setShowAdminPanel(!showAdminPanel)}
            sx={{ mb: 3, border: '2px solid', borderColor: 'warning.main' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'warning.light' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Admin: Section Visibility Controls
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Global Site Controls
              </Typography>
              <Paper sx={{ p: 3, bgcolor: '#12121f', mb: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dashboardEnabled}
                      onChange={handleDashboardToggle}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Members Dashboard Page
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        When disabled, the /dashboard page will show "Coming Soon" to all users
                      </Typography>
                    </Box>
                  }
                />
              </Paper>

              <Box sx={{ borderTop: '2px solid', borderColor: 'grey.300', mb: 4, pt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Quick Sign Navigation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Navigate to another sign while staying in admin mode.
                </Typography>
              </Box>
              <Grid container spacing={1} sx={{ mb: 4 }}>
                {allSigns.map((signName) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={signName}>
                    <Button
                      variant={sign.toLowerCase() === signName ? "contained" : "outlined"}
                      size="small"
                      fullWidth
                      onClick={() => navigate(`/rising/${signName}?admin=true`)}
                      sx={{
                        textTransform: 'capitalize',
                        bgcolor: sign.toLowerCase() === signName ? data.color : 'transparent',
                        borderColor: signData[signName]?.color || 'grey.400',
                        color: sign.toLowerCase() === signName ? 'white' : signData[signName]?.color,
                        '&:hover': {
                          bgcolor: sign.toLowerCase() === signName ? data.color : `${signData[signName]?.color}22`,
                          borderColor: signData[signName]?.color
                        }
                      }}
                    >
                      {signData[signName]?.symbol} {signName}
                    </Button>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ borderTop: '2px solid', borderColor: 'grey.300', mb: 4, pt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Major Astrological Events & Monthly Forecast
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Toggle each section on/off. Disabled sections will show "Coming Soon" and won't be clickable.
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {data.majorTransits.map((transit, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Paper sx={{ p: 2, bgcolor: '#12121f' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={enabledStates[idx] || false}
                            onChange={() => handleToggle(idx)}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {transit.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {transit.subtitle}
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ borderTop: '2px solid', borderColor: 'grey.300', mt: 4, pt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Individual Monthly Forecasts
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Control which months are available in the Monthly Forecast section. Disabled months will show "Coming Soon".
                </Typography>
                <Grid container spacing={2}>
                  {months.map((month) => (
                    <Grid item xs={12} sm={6} md={4} key={month.number}>
                      <Paper sx={{ p: 2, bgcolor: '#12121f' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={monthlyEnabledStates[month.number] || false}
                              onChange={() => handleMonthlyToggle(month.number)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {month.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Free Overview + Extended Available
                              </Typography>
                            </Box>
                          }
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Container>
      )}

      {/* Major Transits */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 }, px: { xs: 2, md: 3 } }}>
        <Typography variant="h4" gutterBottom sx={{
          fontWeight: 700,
          mb: 4,
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
        }}>
          Your 2026 and Beyond Major Astrological Events
        </Typography>

        <Grid container spacing={3}>
          {data.majorTransits.map((transit, idx) => {
            const isEnabled = enabledStates[idx] || false;
            return (
              <Grid item xs={12} sm={6} key={idx}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  whileHover={isEnabled ? { scale: 1.02 } : {}}
                >
                  <Paper
                    sx={{
                      p: 3,
                      height: '100%',
                      borderLeft: `4px solid ${data.color}`,
                      cursor: isEnabled ? 'pointer' : 'default',
                      opacity: 1,
                      bgcolor: '#1a1a2e',
                      transition: 'all 0.3s',
                      '&:hover': isEnabled ? {
                        boxShadow: 6,
                        transform: 'translateY(-2px)'
                      } : {}
                    }}
                    onClick={() => {
                      if (!isEnabled) return;
                      navigate(`/rising/${sign}/${transit.slug}${isAdminMode ? '?admin=true' : ''}`);
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <CalendarTodayIcon sx={{ color: data.color, mr: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {transit.title}
                        </Typography>
                      </Box>
                      {!isEnabled && (
                        <LockIcon sx={{ color: data.color, ml: 1 }} />
                      )}
                    </Box>
                    <Typography component="div">
                      {transit.type === 'overview' ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                          <span>Watch</span>
                          <Chip label="free overview" size="small" sx={{ bgcolor: '#4CAF50', color: 'white', fontWeight: 600 }} />
                          <span>&</span>
                          <span>get</span>
                          <Chip label="extended deep dive" size="small" sx={{ bgcolor: '#c41e3a', color: 'white', fontWeight: 600 }} />
                        </Box>
                      ) : (
                        transit.subtitle
                      )}
                    </Typography>
                    {isEnabled ? (
                      <Button
                        variant="text"
                        sx={{ mt: 2, color: data.color, fontWeight: 600 }}
                        endIcon={<CalendarTodayIcon />}
                      >
                        {transit.type === 'overview' ? 'Watch Now →' : transit.type === 'monthly' ? 'View All Months →' : `View ${sign.charAt(0).toUpperCase() + sign.slice(1)} →`}
                      </Button>
                    ) : (
                      <Chip
                        label="Coming Soon"
                        size="small"
                        sx={{ mt: 2, bgcolor: 'rgba(201,168,124,0.15)', color: '#8a8a9a' }}
                      />
                    )}
                  </Paper>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default RisingSignLanding;